// /app/settings — workspace, team, KVKK + e-Fatura compliance, account.
//
// Visually ported from design-handoff/src/pages/secondary.jsx
// SettingsPage — inline styles + tokens.css (.card, .eyebrow, .input,
// .badge, .btn). Wiring kept intact from the previous Tailwind version
// because the handoff's data was a static stub (Mesh Studio / TEAM / ...)
// and the real identity-service endpoints stay the canonical source.

import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import { useAuth, useSignOut } from "@/lib/auth";
import { useBrands } from "@/hooks/use-persona";
import { PageHead, Tabs, Avatar } from "@/components/primitives";
import { Icons } from "@/components/icons";

export const Route = createFileRoute("/app/settings")({
  component: SettingsPage,
});

type Tab = "workspace" | "team" | "api" | "kvkk" | "efatura" | "account";

function SettingsPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const [tab, setTab] = useState<Tab>("workspace");
  const { user, workspace, hasPassword } = useAuth();
  // API keys are workspace-wide credentials — owner/admin only. The tab
  // simply doesn't render for other roles (backend enforces 403 anyway).
  const canManageApi = user?.role === "owner" || user?.role === "admin";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "12 · Ayarlar" : "12 · Settings"}
        title={tr ? "Workspace, ekip, fatura." : "Workspace, team, billing."}
      />

      <div style={{ padding: "0 32px", borderBottom: "1px solid var(--b1)" }}>
        <Tabs<Tab>
          value={tab}
          onChange={setTab}
          tabs={[
            { id: "workspace", label: tr ? "Workspace" : "Workspace" },
            { id: "team", label: tr ? "Ekip" : "Team" },
            ...(canManageApi ? [{ id: "api" as Tab, label: "API" }] : []),
            { id: "kvkk", label: tr ? "KVKK · Gizlilik" : "KVKK · Privacy" },
            { id: "efatura", label: "e-Fatura" },
            { id: "account", label: tr ? "Hesap" : "Account" },
          ]}
        />
      </div>

      <div
        className="scroll-y"
        style={{
          flex: 1,
          padding: "24px 32px 48px",
          maxWidth: 880,
          margin: "0 auto",
          width: "100%",
        }}
      >
        {tab === "workspace" && <WorkspaceTab workspaceId={workspace?.id} tr={tr} />}
        {tab === "team" && <TeamTab workspaceId={workspace?.id} tr={tr} />}
        {tab === "api" && canManageApi && (
          <ApiTab workspaceId={workspace?.id} workspaceName={workspace?.name} tr={tr} />
        )}
        {tab === "kvkk" && <KVKKTab tr={tr} />}
        {tab === "efatura" && <EFaturaTab workspaceId={workspace?.id} tr={tr} />}
        {tab === "account" && <AccountTab userEmail={user?.email} hasPassword={hasPassword} tr={tr} />}
      </div>
    </div>
  );
}

// ── Workspace tab ──────────────────────────────────────────────────────────

interface WorkspaceShape {
  id: string;
  name: string;
  locale: string;
  timezone: string;
}

// The PATCH body identity-service accepts — `patchWsReq` in
// internal/handlers/workspaces.go. It decodes with DisallowUnknownFields,
// so posting the whole fetched workspace (which carries `id`, `slug`,
// `owner_user_id`, `status`, `created_at`) is a hard 400 "bad json".
// Never spread a server object into this body; project it explicitly.
type WorkspacePatch = Pick<WorkspaceShape, "name" | "locale" | "timezone">;

function workspacePatch(d: WorkspaceShape): WorkspacePatch {
  return { name: d.name, locale: d.locale, timezone: d.timezone };
}

function WorkspaceTab({ workspaceId, tr }: { workspaceId: string | undefined; tr: boolean }) {
  const qc = useQueryClient();
  const { data: ws, isLoading } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => api<WorkspaceShape>(`/workspaces/${workspaceId}`),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });

  const [draft, setDraft] = useState<WorkspaceShape | null>(null);
  if (ws && !draft) setDraft(ws);

  const save = useMutation({
    mutationFn: (body: WorkspacePatch) =>
      api<WorkspaceShape>(`/workspaces/${workspaceId}`, { method: "PATCH", body }),
    onSuccess: (saved) => {
      qc.setQueryData(["workspace", workspaceId], saved);
      qc.invalidateQueries({ queryKey: ["me"] });
      setDraft(saved);
    },
  });

  if (isLoading || !draft || !ws) return <PanelSkeleton />;
  const dirty = JSON.stringify(draft) !== JSON.stringify(ws);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SettingRow label={tr ? "Workspace adı" : "Workspace name"}>
        <input
          className="input"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </SettingRow>
      <SettingRow label={tr ? "Varsayılan dil" : "Default locale"}>
        <select
          className="input"
          value={draft.locale}
          onChange={(e) => setDraft({ ...draft, locale: e.target.value })}
        >
          <option value="tr-TR">Türkçe (Türkiye)</option>
          <option value="en-US">English (US)</option>
        </select>
      </SettingRow>
      <SettingRow label={tr ? "Saat dilimi" : "Timezone"}>
        <input
          className="input"
          value={draft.timezone}
          onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
          placeholder="Europe/Istanbul"
        />
      </SettingRow>

      {save.error instanceof ApiError ? (
        <p style={{ color: "var(--error)", fontSize: 13 }}>{save.error.message}</p>
      ) : null}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8 }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setDraft(ws)}
          disabled={!dirty || save.isPending}
        >
          {tr ? "Vazgeç" : "Cancel"}
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => save.mutate(workspacePatch(draft))}
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? (tr ? "Kaydediliyor…" : "Saving…") : tr ? "Kaydet" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ── Team tab ───────────────────────────────────────────────────────────────

// Roles must mirror identity-service `validRole` (internal/store/store.go):
// owner | admin | editor | approver | viewer. "hr" was offered in the invite
// dropdown but is not in that set — AddWorkspaceMember returned a plain
// `invalid role` error, which the handler maps to a 500, so HR invites always
// failed. "approver" is the real fifth role and previously rendered as an
// empty badge because it was missing from the label/tone maps.
interface Member {
  user_id: string;
  email: string;
  display_name?: string;
  role: "owner" | "admin" | "editor" | "approver" | "viewer";
  joined_at?: string;
  invited_at?: string;
  status: "active" | "invited" | "removed";
}

function TeamTab({ workspaceId, tr }: { workspaceId: string | undefined; tr: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["workspace", workspaceId, "members"],
    queryFn: () =>
      api<{ members: Member[] } | { data: Member[] }>(
        `/workspaces/${workspaceId}/members`,
      ),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
  const members: Member[] =
    (data && "members" in data
      ? data.members
      : data && "data" in data
        ? data.data
        : []) ?? [];

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Member["role"]>("editor");

  const invite = useMutation({
    mutationFn: () =>
      api(`/workspaces/${workspaceId}/members`, {
        method: "POST",
        body: { email: inviteEmail.trim(), role: inviteRole },
      }),
    onSuccess: () => {
      setInviteEmail("");
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId, "members"] });
    },
  });

  const remove = useMutation({
    mutationFn: (uid: string) =>
      api(`/workspaces/${workspaceId}/members/${uid}`, { method: "DELETE" }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId, "members"] }),
  });

  if (isLoading) return <PanelSkeleton />;

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div
        style={{
          padding: 22,
          borderBottom: "1px solid var(--b1)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <h2 style={{ flex: 1, fontSize: 18 }}>
          {members.length} {tr ? "üye" : "members"}
        </h2>
      </div>

      {/* Invite row */}
      <div
        style={{
          padding: 22,
          borderBottom: "1px solid var(--b1)",
          display: "grid",
          gridTemplateColumns: "1fr 180px auto",
          gap: 8,
          alignItems: "center",
        }}
      >
        <input
          className="input"
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          placeholder={tr ? "ornek@firma.com" : "name@company.com"}
        />
        <select
          className="input"
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as Member["role"])}
        >
          <option value="admin">{tr ? "Yönetici" : "Admin"}</option>
          <option value="editor">{tr ? "Editör" : "Editor"}</option>
          <option value="approver">{tr ? "Onaylayıcı" : "Approver"}</option>
          <option value="viewer">{tr ? "Görüntüleyici" : "Viewer"}</option>
        </select>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => invite.mutate()}
          disabled={!inviteEmail.includes("@") || invite.isPending}
        >
          <Icons.Plus size={13} /> {tr ? "Davet et" : "Invite"}
        </button>
      </div>
      {invite.error instanceof ApiError ? (
        <div style={{ padding: "0 22px 14px", color: "var(--error)", fontSize: 12 }}>
          {invite.error.message}
        </div>
      ) : null}

      {/* Members list */}
      {members.length === 0 ? (
        <div style={{ padding: 22, color: "var(--mu)", fontSize: 13, textAlign: "center" }}>
          {tr ? "Henüz üye yok." : "No members yet."}
        </div>
      ) : (
        members.map((m) => (
          <div
            key={m.user_id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "14px 22px",
              borderBottom: "1px solid var(--b1)",
            }}
          >
            <Avatar name={m.display_name || m.email} size={32} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {m.display_name || m.email}
              </div>
              <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                {m.email}
              </div>
            </div>
            <RoleBadge role={m.role} tr={tr} />
            {m.status === "invited" ? (
              <span className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                {tr ? "bekliyor" : "pending"}
              </span>
            ) : null}
            {m.role !== "owner" ? (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  if (
                    confirm(
                      tr
                        ? `${m.email} bu workspace'ten çıkarılsın mı?`
                        : `Remove ${m.email} from this workspace?`,
                    )
                  )
                    remove.mutate(m.user_id);
                }}
              >
                {tr ? "Çıkar" : "Remove"}
              </button>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
}

function RoleBadge({ role, tr }: { role: Member["role"]; tr: boolean }) {
  const labels: Record<Member["role"], string> = tr
    ? {
        owner: "Kurucu",
        admin: "Yönetici",
        editor: "Editör",
        approver: "Onaylayıcı",
        viewer: "Görüntüleyici",
      }
    : {
        owner: "Owner",
        admin: "Admin",
        editor: "Editor",
        approver: "Approver",
        viewer: "Viewer",
      };
  const tones: Record<Member["role"], string> = {
    owner: "badge-red",
    admin: "badge-blue",
    editor: "badge-green",
    approver: "badge-amber",
    viewer: "badge-gray",
  };
  // Unknown role from a newer backend → show it verbatim rather than an
  // empty grey pill.
  return (
    <span className={`badge ${tones[role] ?? "badge-gray"}`}>
      <span className="dot" />
      {labels[role] ?? role}
    </span>
  );
}

// ── API tab ────────────────────────────────────────────────────────────────
//
// Self-serve surface for the knowledge-ingest API (brand-knowledge-
// freshness plan §5 / Tier 2). Owner/admin only. Three sections:
//   1. API keys — list / create (key shown ONCE) / rotate / revoke,
//      backed by identity-service /v1/workspaces/{id}/api-keys.
//   2. "Instructions for your AI" — a fully DYNAMIC copy-paste block
//      with the workspace's real brands + ids, built from live /brands
//      data. This is the centerpiece: paste it into any AI session and
//      that AI can use the API correctly with zero extra context.
//   3. Human-readable endpoint reference (tr+en), rendered in-panel.
//
// brand-service v0.5.84 widened the surface: besides the direct-write
// /ingest/updates, a key can now READ a brand's knowledge and PROPOSE
// products/services/topics/freeform text. Proposals answer 202 and land in
// the brand's Knowledge tab for approval, so both the AI block and the
// reference must say plainly that a 202 means "nothing changed yet" —
// otherwise an agent retries a write that is merely waiting for a human.

const INGEST_BASE_URL = "https://api.nexusdijital.com/v1/ingest";
const INGEST_API_URL = `${INGEST_BASE_URL}/updates`;
const INGEST_DOCS_URL = "https://api.nexusdijital.com/v1/ingest/docs";

interface ApiKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  brand_ids: string[];
  rate_limit_rpm: number;
  last_used_at: string | null;
  expires_at: string;
  revoked_at?: string | null; // set (future) ⇒ rotation grace window
  created_at: string;
}

interface ApiKeyCreateResp {
  api_key: ApiKeyRow;
  key: string; // full secret — shown once, never retrievable again
  previous_valid_until?: string;
}

function fmtDate(iso: string | null | undefined, tr: boolean): string {
  if (!iso) return tr ? "hiç" : "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(tr ? "tr-TR" : "en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
}

function CopyButton({ text, tr, label }: { text: string; tr: boolean; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn btn-secondary btn-sm"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? (tr ? "✓ Kopyalandı" : "✓ Copied") : (label ?? (tr ? "Kopyala" : "Copy"))}
    </button>
  );
}

function ApiTab({
  workspaceId,
  workspaceName,
  tr,
}: {
  workspaceId: string | undefined;
  workspaceName: string | undefined;
  tr: boolean;
}) {
  const qc = useQueryClient();
  const keysKey = ["workspace", workspaceId, "api-keys"] as const;

  const { data, isLoading } = useQuery({
    queryKey: keysKey,
    queryFn: () => api<{ api_keys: ApiKeyRow[] }>(`/workspaces/${workspaceId}/api-keys`),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
  const keys = data?.api_keys ?? [];
  const brandsQ = useBrands();
  const brands = brandsQ.data?.data ?? [];

  // Freshly minted secret — the ONE place it is ever visible.
  const [freshKey, setFreshKey] = useState<ApiKeyCreateResp | null>(null);
  const [newName, setNewName] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api<ApiKeyCreateResp>(`/workspaces/${workspaceId}/api-keys`, {
        method: "POST",
        body: { name: newName.trim() },
      }),
    onSuccess: (r) => {
      setFreshKey(r);
      setNewName("");
      void qc.invalidateQueries({ queryKey: keysKey });
    },
  });

  const rotate = useMutation({
    mutationFn: (kid: string) =>
      api<ApiKeyCreateResp>(`/workspaces/${workspaceId}/api-keys/${kid}/rotate`, {
        method: "POST",
      }),
    onSuccess: (r) => {
      setFreshKey(r);
      void qc.invalidateQueries({ queryKey: keysKey });
    },
  });

  const revoke = useMutation({
    mutationFn: (kid: string) =>
      api<void>(`/workspaces/${workspaceId}/api-keys/${kid}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keysKey }),
  });

  // Structured 402 from identity-service → upgrade prompt, not a raw error.
  const createErr = create.error instanceof ApiError ? create.error : null;
  const limitBody = (createErr?.status === 402
    ? (createErr.body as unknown as {
        message?: string; plan_slug?: string; max_api_keys?: number;
      })
    : null);

  // ---- the dynamic AI-instructions block --------------------------------
  const brandLines = brands
    .map((b) => `- ${b.trade_name} -> brand_id: ${b.id}`)
    .join("\n");
  const aiInstructions = `# Nexus Dijital knowledge-ingest API — instructions for an AI assistant

You can keep the brand knowledge of the workspace "${workspaceName ?? ""}"
up to date inside Nexus Dijital, the platform that generates and publishes
this workspace's social-media posts. Whatever ends up in that knowledge is
what the post generator says about the brand — caption and image.

Base URL:
  ${INGEST_BASE_URL}

Headers:
  Authorization: Bearer <PASTE YOUR API KEY>          // every request
  Content-Type: application/json                       // every request with a body
  Idempotency-Key: <stable unique string per POST, e.g. "sso-launch-2026-08-16">  // every POST

Brands in this workspace (use the matching brand_id):
${brandLines || "- (no brands yet — create one in the dashboard first)"}

## Two kinds of write — read this first

1. "What's new" updates (POST /updates) are written DIRECTLY and start
   steering posts immediately: 201 created.
2. EVERYTHING ELSE (text, products, services, topics) is a PROPOSAL. You
   get 202 accepted-for-review with a batch_id, and the change waits in
   the brand's Knowledge tab in the dashboard until a human approves it.
   Nothing you propose reaches the brand before that. If a proposal seems
   to have "done nothing", it is waiting for approval — poll the batch,
   do not re-send it.

## Read the current state

  GET ${INGEST_BASE_URL}/brands
    → the brands this key may write to.
  GET ${INGEST_BASE_URL}/brands/{brand_id}/knowledge
    → the brand's current products, services, topics and updates WITH
      their ids. Read this before proposing an update or a delete so you
      target the row that already exists instead of duplicating it.

## Propose changes (each returns 202 + batch_id)

  POST ${INGEST_BASE_URL}/text
    {"brand_id":"<uuid>","text":"<any prose about the brand>"}
    The AI itemizes the text into products / services / topics / brand
    voice and proposes what it found.

  POST ${INGEST_BASE_URL}/products
    {"brand_id":"<uuid>",
     "operation":"create|update|delete",   // optional, default "create"
     "target_id":"<product uuid>",         // optional — the name works as a key
     "name":"...", "description":"...", "category":"...", "features":["..."]}
    A "create" whose name already exists becomes an update of that row,
    never a duplicate.

  POST ${INGEST_BASE_URL}/services
    Same shape, with "target_audience" and "price_hint" in place of
    "category" and "features".

  POST ${INGEST_BASE_URL}/topics
    {"brand_id":"<uuid>","operation":"create|update|delete",
     "target_id":"<topic uuid>","topic":"...","mode":"include|avoid"}
    mode "avoid" means a subject the AI must NEVER mention — those always
    need an explicit human tick, so propose them sparingly and precisely.

## Write directly

  POST ${INGEST_API_URL}
    {"brand_id":"<uuid>",                              // required
     "kind":"launch|feature|pricing|milestone|event|general", // required
     "headline":"<= 120 chars, one factual sentence",  // required
     "detail":"<= 400 chars extra context",            // optional
     "occurred_on":"YYYY-MM-DD",                       // optional, default today
     "expires_on":"YYYY-MM-DD"}                        // optional, default +90 days
    201 = created (influences posts at once); 200 + "deduplicated": true =
    same brand+headline+date already exists (safe retry).

  DELETE ${INGEST_API_URL}/{update_id}?brand_id=<uuid>
    Removes a what's-new entry.

## Check what happened

  GET ${INGEST_BASE_URL}/batches/{batch_id}
    → status: pending | applied | partially_applied | rejected | expired.

Examples:
  curl -sS -X POST ${INGEST_API_URL} \\
    -H "Authorization: Bearer <PASTE YOUR API KEY>" \\
    -H "Idempotency-Key: sso-launch-2026-08-16" \\
    -H "Content-Type: application/json" \\
    -d '{"brand_id":"${brands[0]?.id ?? "<brand uuid>"}","kind":"feature","headline":"Shipped SAML SSO","detail":"Enterprise customers can now log in through their own identity provider."}'

  curl -sS -X POST ${INGEST_BASE_URL}/products \\
    -H "Authorization: Bearer <PASTE YOUR API KEY>" \\
    -H "Idempotency-Key: product-filter-v2-2026-08-16" \\
    -H "Content-Type: application/json" \\
    -d '{"brand_id":"${brands[0]?.id ?? "<brand uuid>"}","name":"Filter V2","description":"Second-generation water filter, 30% faster flow."}'

Errors use {"error":{"code","message"}}: 401 bad key, 400 validation
(details.fields[]), 404 brand or row not in this workspace, 409 conflict,
429 rate limit (60/min, honour Retry-After).

What to send: only things that are TRUE, SHIPPED and CUSTOMER-VISIBLE —
a live feature, a launch, a pricing change, a milestone, a dated event
(set expires_on to the day after), a real product or service. Never send
commits/refactors, unreleased work, secrets or internal metrics: anything
accepted may appear word-for-word in published social posts. A few pushes
per month is healthy; the generator only reads the 5 newest live updates
per brand.

Full machine-readable reference (no auth): ${INGEST_DOCS_URL}`;

  if (isLoading) return <PanelSkeleton />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── 1. Keys ── */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 22, borderBottom: "1px solid var(--b1)" }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            — {tr ? "API anahtarları" : "API keys"}
          </div>
          <p style={{ color: "var(--tx2)", fontSize: 13, lineHeight: 1.7 }}>
            {tr
              ? "Bilgi-besleme API'si (knowledge ingest) için workspace'e özel anahtarlar. Anahtar yalnızca kendi workspace'indeki markaların bilgilerini okuyabilir, güncelleme yazabilir ve değişiklik önerebilir; önerilen değişiklikler markanın Bilgi sekmesinde onaylanana kadar uygulanmaz. Anahtar hiçbir şey yayınlayamaz. 365 gün sonra otomatik süresi dolar."
              : "Workspace-scoped keys for the knowledge-ingest API. A key can read the knowledge of brands in its own workspace, write updates and propose changes — proposals stay inert until someone approves them in the brand's Knowledge tab. A key can publish nothing. Keys hard-expire after 365 days."}
          </p>
        </div>

        {/* Create row */}
        <div
          style={{
            padding: 22,
            borderBottom: "1px solid var(--b1)",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 8,
            alignItems: "center",
          }}
        >
          <input
            className="input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={tr ? "Anahtar adı — ör. \"CI pipeline\"" : "Key name — e.g. \"CI pipeline\""}
            maxLength={100}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => create.mutate()}
            disabled={!newName.trim() || create.isPending}
          >
            <Icons.Plus size={13} /> {tr ? "Anahtar oluştur" : "Create key"}
          </button>
        </div>

        {limitBody ? (
          <div
            style={{
              margin: "0 22px 14px", padding: 12, borderRadius: 8,
              background: "rgba(245,158,11,0.08)", color: "var(--warning)", fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            {tr
              ? `Plan limiti: ${limitBody.message ?? "API anahtarı limitine ulaşıldı."} `
              : `Plan limit: ${limitBody.message ?? "API key limit reached."} `}
            <a href="/app/billing" style={{ color: "var(--accent)" }}>
              {tr ? "Planı yükselt →" : "Upgrade plan →"}
            </a>
          </div>
        ) : createErr ? (
          <div style={{ margin: "0 22px 14px", color: "var(--error)", fontSize: 12 }}>
            {createErr.message}
          </div>
        ) : null}

        {/* Fresh secret — shown once */}
        {freshKey ? (
          <div
            style={{
              margin: "0 22px 18px", padding: 16, borderRadius: 10,
              border: "1px solid var(--accent)", background: "rgba(59,130,246,0.06)",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              {tr ? "Anahtarınız hazır — ŞİMDİ kaydedin" : "Your key is ready — store it NOW"}
            </div>
            <p style={{ fontSize: 12, color: "var(--tx2)", marginBottom: 10, lineHeight: 1.6 }}>
              {tr
                ? "Bu anahtar yalnızca bir kez gösterilir. Bu pencereyi kapattıktan sonra bir daha görüntülenemez — güvenli bir yere (parola yöneticisi, secret manager) kaydedin."
                : "This key is shown only once. After you leave this screen it can never be viewed again — store it somewhere safe (password manager, secret manager)."}
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code
                className="mono"
                style={{
                  flex: 1, padding: "10px 12px", borderRadius: 6, fontSize: 12,
                  background: "var(--bg)", border: "1px solid var(--b1)",
                  overflowX: "auto", whiteSpace: "nowrap",
                }}
              >
                {freshKey.key}
              </code>
              <CopyButton text={freshKey.key} tr={tr} />
            </div>
            {freshKey.previous_valid_until ? (
              <p className="mono" style={{ fontSize: 10, color: "var(--mu)", marginTop: 8 }}>
                {tr
                  ? `Eski anahtar ${fmtDate(freshKey.previous_valid_until, tr)} tarihine kadar (24 saat) çalışmaya devam eder.`
                  : `The old key keeps working until ${fmtDate(freshKey.previous_valid_until, tr)} (24-hour grace).`}
              </p>
            ) : null}
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginTop: 10 }}
              onClick={() => setFreshKey(null)}
            >
              {tr ? "Kaydettim, kapat" : "I stored it — close"}
            </button>
          </div>
        ) : null}

        {/* Key list */}
        {keys.length === 0 ? (
          <div style={{ padding: 22, color: "var(--mu)", fontSize: 13, textAlign: "center" }}>
            {tr ? "Henüz API anahtarı yok." : "No API keys yet."}
          </div>
        ) : (
          keys.map((k) => (
            <div
              key={k.id}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px 22px", borderBottom: "1px solid var(--b1)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {k.name}
                  {k.revoked_at ? (
                    <span className="badge badge-amber" style={{ marginLeft: 8 }}>
                      {tr ? "rotasyon — eski anahtar" : "rotating — old key"}
                    </span>
                  ) : null}
                </div>
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)", marginTop: 2 }}>
                  {k.key_prefix}…{" · "}
                  {tr ? "oluşturuldu" : "created"} {fmtDate(k.created_at, tr)}
                  {" · "}
                  {tr ? "son kullanım" : "last used"} {fmtDate(k.last_used_at, tr)}
                  {" · "}
                  {tr ? "son geçerlilik" : "expires"} {fmtDate(k.expires_at, tr)}
                </div>
              </div>
              {!k.revoked_at ? (
                <>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      if (
                        confirm(
                          tr
                            ? `"${k.name}" için yeni bir gizli anahtar üretilsin mi? Eski anahtar 24 saat daha çalışır.`
                            : `Generate a new secret for "${k.name}"? The old key keeps working for 24 hours.`,
                        )
                      )
                        rotate.mutate(k.id);
                    }}
                    disabled={rotate.isPending}
                  >
                    {tr ? "Yenile" : "Rotate"}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: "var(--error)" }}
                    onClick={() => {
                      if (
                        confirm(
                          tr
                            ? `"${k.name}" hemen iptal edilsin mi? Bu anahtarı kullanan tüm sistemler ANINDA erişimi kaybeder.`
                            : `Revoke "${k.name}" immediately? Anything using this key loses access AT ONCE.`,
                        )
                      )
                        revoke.mutate(k.id);
                    }}
                    disabled={revoke.isPending}
                  >
                    {tr ? "İptal et" : "Revoke"}
                  </button>
                </>
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* ── 2. Instructions for your AI (dynamic centerpiece) ── */}
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>
          — {tr ? "Yapay zekânız için talimatlar" : "Instructions for your AI"}
        </div>
        <p style={{ color: "var(--tx2)", fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>
          {tr ? (
            <>
              Aşağıdaki bloğu kopyalayıp ChatGPT, Claude veya kendi AI oturumunuza
              yapıştırın — <strong>bu workspace'in gerçek marka listesiyle</strong> üretildi;
              AI başka hiçbir bilgiye ihtiyaç duymadan API'yi doğru kullanabilir.
              Tek yapmanız gereken <code className="mono">{"<PASTE YOUR API KEY>"}</code> yerine
              yukarıda oluşturduğunuz anahtarı koymak.
            </>
          ) : (
            <>
              Copy the block below into ChatGPT, Claude or your own AI session — it is
              generated with <strong>this workspace's real brand list</strong>, so the AI can
              use the API correctly with zero extra context. The only thing you do is replace{" "}
              <code className="mono">{"<PASTE YOUR API KEY>"}</code> with a key created above.
            </>
          )}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <CopyButton
            text={aiInstructions}
            tr={tr}
            label={tr ? "Talimatları kopyala" : "Copy instructions"}
          />
        </div>
        <textarea
          className="input mono"
          readOnly
          value={aiInstructions}
          style={{
            width: "100%", minHeight: 320, fontSize: 11, lineHeight: 1.6,
            resize: "vertical", whiteSpace: "pre", overflowX: "auto",
          }}
          onFocus={(e) => e.currentTarget.select()}
        />
        <p className="mono" style={{ fontSize: 10, color: "var(--mu)", marginTop: 8 }}>
          {tr
            ? "Ayrıca herhangi bir AI ajanı şu adresten kimlik doğrulamasız tam referansı okuyabilir: "
            : "Any AI agent can also fetch the full reference, no auth required, from: "}
          <a href={INGEST_DOCS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
            {INGEST_DOCS_URL}
          </a>
        </p>
      </div>

      {/* ── 3. Endpoint reference (human-readable, in-panel) ── */}
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>
          — {tr ? "Uç nokta referansı" : "Endpoint reference"}
        </div>
        <div style={{ fontSize: 13, color: "var(--tx2)", lineHeight: 1.8 }}>
          <p style={{ marginBottom: 10 }}>
            <code className="mono" style={{ fontSize: 12, color: "var(--tx)" }}>
              {INGEST_BASE_URL}
            </code>
          </p>
          <p style={{ marginBottom: 10 }}>
            {tr
              ? "İki tür yazma var. \"Yenilik\" kayıtları (POST /updates) doğrudan yazılır ve hemen etkiler; diğer her şey ÖNERİdir — 202 (incelemeye alındı) döner ve marka sayfasındaki Bilgi sekmesinde onaylanana kadar hiçbir şey değişmez."
              : "There are two kinds of write. \"What's new\" entries (POST /updates) are written directly and take effect at once; everything else is a PROPOSAL — it returns 202 accepted for review and changes nothing until it is approved in the brand's Knowledge tab."}
          </p>
          {/* Endpoint map. Paths are relative to the base URL above so the
              rows stay readable at 12px on a narrow settings column. */}
          <table
            style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginBottom: 14 }}
          >
            <tbody>
              {(
                [
                  [
                    "GET /brands",
                    tr
                      ? "Bu anahtarın yazabildiği markalar"
                      : "Brands this key may write to",
                  ],
                  [
                    "GET /brands/{id}/knowledge",
                    tr
                      ? "Markanın mevcut ürün / hizmet / konu / güncellemeleri — id'leriyle birlikte"
                      : "The brand's current products / services / topics / updates, with their ids",
                  ],
                  [
                    "POST /text",
                    tr
                      ? "Serbest metin; AI ürün, hizmet, konu ve marka sesi çıkarır → öneri (202)"
                      : "Freeform text; the AI extracts products, services, topics and voice → proposal (202)",
                  ],
                  [
                    "POST /products",
                    tr
                      ? "{brand_id, operation?, target_id?, name?, description?, category?, features?} — operation varsayılanı create; adı zaten var olan bir create, kopya yerine güncellemeye dönüşür; target_id yerine ad doğal anahtar olarak kullanılabilir → öneri (202)"
                      : "{brand_id, operation?, target_id?, name?, description?, category?, features?} — operation defaults to create; a create whose name already exists becomes an update, not a duplicate; the name works as a natural key when target_id is omitted → proposal (202)",
                  ],
                  [
                    "POST /services",
                    tr
                      ? "Aynı gövde; category / features yerine target_audience / price_hint → öneri (202)"
                      : "Same shape, with target_audience / price_hint instead of category / features → proposal (202)",
                  ],
                  [
                    "POST /topics",
                    tr
                      ? "{brand_id, operation?, target_id?, topic?, mode?: include|avoid} → öneri (202)"
                      : "{brand_id, operation?, target_id?, topic?, mode?: include|avoid} → proposal (202)",
                  ],
                  [
                    "POST /updates",
                    tr
                      ? "Doğrudan yazar (201 / 200) — alanlar aşağıda"
                      : "Writes directly (201 / 200) — fields below",
                  ],
                  [
                    "DELETE /updates/{id}?brand_id=…",
                    tr ? "Bir yenilik kaydını siler" : "Removes a what's-new entry",
                  ],
                  [
                    "GET /batches/{id}",
                    tr
                      ? "Bir önerinin durumu: pending | applied | partially_applied | rejected | expired"
                      : "A proposal batch's status: pending | applied | partially_applied | rejected | expired",
                  ],
                ] as [string, string][]
              ).map(([k, v]) => (
                <tr key={k} style={{ borderBottom: "1px solid var(--b1)" }}>
                  <td
                    className="mono"
                    style={{
                      padding: "6px 10px 6px 0",
                      color: "var(--tx)",
                      whiteSpace: "nowrap",
                      verticalAlign: "top",
                    }}
                  >
                    {k}
                  </td>
                  <td style={{ padding: "6px 0" }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginBottom: 10 }}>
            <code className="mono" style={{ fontSize: 12, color: "var(--tx)" }}>
              POST {INGEST_API_URL}
            </code>
          </p>
          <p style={{ marginBottom: 10 }}>
            {tr
              ? "Markanız için \"yenilik\" bilgileri (çıkan özellik, lansman, fiyat değişikliği…) gönderir. Gönderilen her kayıt ~90 gün boyunca üretilen gönderileri yönlendirir; süresi dolunca kendiliğinden devre dışı kalır. Kayıtlar marka sayfasındaki Bilgi sekmesinde \"api\" rozetiyle görünür ve oradan silinebilir."
              : "Pushes \"what's new\" facts for your brand (shipped feature, launch, pricing change…). Each entry steers generated posts for ~90 days, then silently expires. Entries appear in the brand's Knowledge tab with an \"api\" badge and can be deleted there."}
          </p>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <tbody>
              {(
                [
                  ["Authorization", tr ? "Bearer <API anahtarı> — zorunlu" : "Bearer <API key> — required"],
                  ["Idempotency-Key", tr ? "istek başına benzersiz sabit dize — tüm POST'larda zorunlu" : "stable unique string per request — required on every POST"],
                  ["brand_id", tr ? "workspace'inizdeki marka UUID'si — zorunlu" : "UUID of a brand in your workspace — required"],
                  ["kind", "launch | feature | pricing | milestone | event | general"],
                  ["headline", tr ? "en fazla 120 karakter — zorunlu" : "max 120 characters — required"],
                  ["detail", tr ? "en fazla 400 karakter — isteğe bağlı" : "max 400 characters — optional"],
                  ["occurred_on", tr ? "YYYY-AA-GG; varsayılan bugün" : "YYYY-MM-DD; defaults to today"],
                  ["expires_on", tr ? "YYYY-AA-GG; varsayılan +90 gün" : "YYYY-MM-DD; defaults to +90 days"],
                ] as [string, string][]
              ).map(([k, v]) => (
                <tr key={k} style={{ borderBottom: "1px solid var(--b1)" }}>
                  <td className="mono" style={{ padding: "6px 8px 6px 0", color: "var(--tx)", whiteSpace: "nowrap", verticalAlign: "top" }}>
                    {k}
                  </td>
                  <td style={{ padding: "6px 0" }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: 10, fontSize: 12 }}>
            {tr ? (
              <>
                <strong>Yanıtlar:</strong> 202 incelemeye alındı (öneri uç noktaları — batch_id döner,
                marka Bilgi sekmesinde onay bekler) · 201 oluşturuldu ·
                200 + deduplicated (aynı marka+başlık+tarih tekrarı) ·
                401 geçersiz anahtar · 400 doğrulama hatası · 404 marka bu workspace'te değil ·
                429 hız limiti (dakikada 60, <code className="mono">Retry-After</code> başlığına uyun).
              </>
            ) : (
              <>
                <strong>Responses:</strong> 202 accepted for review (proposal endpoints — returns a
                batch_id and waits for approval in the brand's Knowledge tab) · 201 created ·
                200 + deduplicated (replay of same brand+headline+date) ·
                401 invalid key · 400 validation failure · 404 brand not in this workspace ·
                429 rate limited (60/minute, honour <code className="mono">Retry-After</code>).
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── KVKK tab ───────────────────────────────────────────────────────────────
//
// Compliance reference per the standing memory (Mesh Yazılım Teknoloji
// Ltd. Şti. + Trademark 2024/161525). All-static for v0.1; the wiring
// for consent state lands with dedicated KVKK endpoints (planned v0.3.8+).

function KVKKTab({ tr }: { tr: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Data controller */}
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Veri sorumlusu" : "Data controller"}
        </div>
        <h2 style={{ marginBottom: 8, fontSize: 18 }}>
          VERBİS · {tr ? "başvuru yapıldı" : "registration pending"} · 2024/16125
        </h2>
        <p
          style={{
            color: "var(--tx2)",
            fontSize: 13,
            lineHeight: 1.7,
            marginBottom: 18,
          }}
        >
          {tr ? (
            <>
              <strong>Mesh Yazılım Teknoloji Ltd. Şti.</strong> kayıtlı veri sorumlusudur. Aydınlatma Metni ve Açık Rıza formlarımız her operatör ve müşteri temas noktasında versiyonlanır ve imzalanır.
            </>
          ) : (
            <>
              <strong>Mesh Yazılım Teknoloji Ltd. Şti.</strong> is a registered data controller. Our Aydınlatma Metni and Açık Rıza forms are versioned and signed by every operator and customer touchpoint.
            </>
          )}
        </p>
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "180px 1fr",
            rowGap: 8,
            columnGap: 16,
            fontSize: 13,
          }}
        >
          <dt style={{ color: "var(--tx2)" }}>{tr ? "Ticari unvan" : "Legal name"}</dt>
          <dd style={{ color: "var(--tx)", fontWeight: 500 }}>
            Mesh Yazılım Teknoloji Ltd. Şti.
          </dd>
          <dt style={{ color: "var(--tx2)" }}>{tr ? "Marka" : "Brand"}</dt>
          <dd style={{ color: "var(--tx)", fontWeight: 500 }}>
            Nexus Dijital{" "}
            <span className="mono" style={{ color: "var(--mu)", fontSize: 11 }}>
              (TÜRKPATENT · 2024/161525)
            </span>
          </dd>
          <dt style={{ color: "var(--tx2)" }}>VKN</dt>
          <dd className="mono" style={{ color: "var(--tx)" }}>6230721254</dd>
          <dt style={{ color: "var(--tx2)" }}>{tr ? "Vergi dairesi" : "Tax office"}</dt>
          <dd style={{ color: "var(--tx)" }}>Suadiye</dd>
        </dl>
      </div>

      {/* Documents */}
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Belgeler" : "Documents"}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <DocButton
            href="https://nexusdijital.com/aydinlatma-metni"
            label={tr ? "Aydınlatma Metni" : "Privacy notice"}
          />
          <DocButton
            href="https://nexusdijital.com/gizlilik"
            label={tr ? "Gizlilik Politikası" : "Privacy policy"}
          />
          <DocButton
            href="https://nexusdijital.com/kullanim-sartlari"
            label={tr ? "Kullanım Şartları" : "Terms of use"}
          />
          <DocButton
            href="https://nexusdijital.com/cerez-politikasi"
            label={tr ? "Çerez Politikası" : "Cookie policy"}
          />
        </div>
      </div>

      {/* My data */}
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Verilerim" : "My data"}
        </div>
        <p style={{ color: "var(--tx2)", fontSize: 13, marginBottom: 14 }}>
          {tr
            ? "KVKK 11. madde kapsamındaki haklarını buradan kullanabilirsin."
            : "Exercise your rights under KVKK Article 11 here."}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button
            className="btn btn-secondary btn-sm"
            disabled
            title={tr ? "Yakında" : "Coming soon"}
          >
            {tr ? "Verilerimi dışa aktar" : "Export my data"}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            disabled
            title={tr ? "Yakında" : "Coming soon"}
          >
            {tr ? "Hesabımı sil" : "Delete my account"}
          </button>
        </div>
        <p
          className="mono"
          style={{ color: "var(--mu)", fontSize: 10, marginTop: 14, lineHeight: 1.6 }}
        >
          {tr
            ? "Silme talebinden sonra hesabın 30 gün içinde tamamen kaldırılır. Bu süre içinde tekrar giriş yaparak iptal edebilirsin."
            : "After deletion, your account is fully removed within 30 days. You can cancel by signing in during that window."}
        </p>
      </div>

      {/* Contact */}
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "İletişim" : "Contact"}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
          <div>
            <span style={{ color: "var(--tx2)" }}>
              {tr ? "KVKK başvuruları:" : "KVKK requests:"}
            </span>{" "}
            <a href="mailto:kvkk@nexusdijital.com" style={{ color: "var(--accent)" }}>
              kvkk@nexusdijital.com
            </a>
          </div>
          <div>
            <span style={{ color: "var(--tx2)" }}>
              {tr ? "Veri Sorumlusu (DPO):" : "Data Protection Officer (DPO):"}
            </span>{" "}
            <a href="mailto:dpo@nexusdijital.com" style={{ color: "var(--accent)" }}>
              dpo@nexusdijital.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocButton({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
      <Icons.ArrowRight size={13} /> {label}
    </a>
  );
}

// ── e-Fatura tab ───────────────────────────────────────────────────────────

// e-Fatura tab — wired to billing-service /v1/billing/tax-id (Phase 6).
// VKN (10 digits) for businesses, TCKN (11 digits) for individuals.
// Backend runs the GIB checksum locally; we render the validation
// status (✓ verified · ⚠ format error · ⚠ checksum error) inline.

type TaxIDRow = {
  id: string;
  kind: string; // "VKN" | "TCKN"
  value: string;
  is_validated: boolean;
};

type TaxIDUpsertResp = {
  kind: string;
  value: string;
  is_validated: boolean;
  format?: "valid" | "invalid_format" | "invalid_checksum";
  message?: string;
};

function EFaturaTab({
  workspaceId,
  tr,
}: {
  workspaceId: string | undefined;
  tr: boolean;
}) {
  const qc = useQueryClient();
  const { data: taxIDs } = useQuery({
    queryKey: ["billing", "tax-ids"],
    queryFn: () => api<{ tax_ids: TaxIDRow[] }>("/billing/tax-id"),
  });
  const existing = (taxIDs?.tax_ids ?? []).reduce<Record<string, TaxIDRow>>((acc, r) => {
    acc[r.kind] = r;
    return acc;
  }, {});

  const existingVKN = existing["VKN"];
  const existingTCKN = existing["TCKN"];
  const [isBusiness, setIsBusiness] = useState<boolean>(existingTCKN ? false : true);
  const [vkn, setVkn] = useState(existingVKN?.value ?? "");
  const [tckn, setTckn] = useState(existingTCKN?.value ?? "");
  const [result, setResult] = useState<TaxIDUpsertResp | null>(null);

  const upsert = useMutation({
    mutationFn: (body: { kind: "VKN" | "TCKN"; value: string }) =>
      api<TaxIDUpsertResp>("/billing/tax-id", { method: "POST", body }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["billing", "tax-ids"] });
    },
  });

  const del = useMutation({
    mutationFn: (kind: "VKN" | "TCKN") =>
      api<void>(`/billing/tax-id/${kind}`, { method: "DELETE" }),
    onSuccess: () => {
      setResult(null);
      qc.invalidateQueries({ queryKey: ["billing", "tax-ids"] });
    },
  });

  // Sync local form when fresh data arrives.
  useEffect(() => {
    if (existingVKN && !vkn) setVkn(existingVKN.value);
    if (existingTCKN && !tckn) setTckn(existingTCKN.value);
  }, [existingVKN, existingTCKN, vkn, tckn]);

  const onSave = () => {
    setResult(null);
    if (isBusiness) {
      if (vkn.length !== 10) return;
      upsert.mutate({ kind: "VKN", value: vkn });
    } else {
      if (tckn.length !== 11) return;
      upsert.mutate({ kind: "TCKN", value: tckn });
    }
  };

  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="eyebrow" style={{ marginBottom: 14 }}>
        — e-Fatura
      </div>
      <p style={{ color: "var(--tx2)", fontSize: 13, lineHeight: 1.7, marginBottom: 18 }}>
        {tr
          ? "Türkiye'de fatura kesimimiz için fatura bilgilerinizi girin. Tüzel kişiler için e-Fatura, bireysel kullanıcılar için e-Arşiv keserek tahsilat sonrası e-posta ile iletiriz."
          : "Provide your billing details for Turkish invoicing. We issue e-Fatura for businesses and e-Arşiv for individuals, delivered by email after each charge."}
      </p>

      <div style={{ display: "flex", gap: 18, marginBottom: 16 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input
            type="radio"
            checked={isBusiness}
            onChange={() => { setIsBusiness(true); setResult(null); }}
            style={{ accentColor: "var(--accent)" }}
          />
          {tr ? "Tüzel kişi" : "Business"}
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input
            type="radio"
            checked={!isBusiness}
            onChange={() => { setIsBusiness(false); setResult(null); }}
            style={{ accentColor: "var(--accent)" }}
          />
          {tr ? "Bireysel" : "Individual"}
        </label>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {isBusiness ? (
          <SettingRow label="VKN (Vergi Kimlik No)">
            <input
              className="input"
              value={vkn}
              onChange={(e) => setVkn(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder={tr ? "10 haneli VKN" : "10-digit VKN"}
              inputMode="numeric"
              maxLength={10}
            />
            {existingVKN ? <TaxIDStatusInline row={existingVKN} tr={tr} /> : null}
          </SettingRow>
        ) : (
          <SettingRow label="TCKN">
            <input
              className="input"
              value={tckn}
              onChange={(e) => setTckn(e.target.value.replace(/\D/g, "").slice(0, 11))}
              placeholder={tr ? "11 haneli TC kimlik" : "11-digit TC ID"}
              inputMode="numeric"
              maxLength={11}
            />
            {existingTCKN ? <TaxIDStatusInline row={existingTCKN} tr={tr} /> : null}
          </SettingRow>
        )}
      </div>

      {/* Inline validation feedback from the upsert response */}
      {result ? (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 6,
            background:
              result.format === "valid"
                ? "rgba(34,197,94,0.08)"
                : "rgba(239,68,68,0.08)",
            color:
              result.format === "valid" ? "var(--success)" : "var(--error)",
            fontSize: 12,
          }}
        >
          {result.format === "valid"
            ? (tr ? "✓ Doğrulandı" : "✓ Validated")
            : `⚠ ${result.message ?? (tr ? "Geçersiz" : "Invalid")}`}
        </div>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 14, gap: 10 }}>
        {(isBusiness && existingVKN) || (!isBusiness && existingTCKN) ? (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => del.mutate(isBusiness ? "VKN" : "TCKN")}
            disabled={del.isPending}
            style={{ color: "var(--error)" }}
          >
            {tr ? "Sil" : "Remove"}
          </button>
        ) : <span />}
        <button
          className="btn btn-primary btn-sm"
          onClick={onSave}
          disabled={upsert.isPending || (isBusiness ? vkn.length !== 10 : tckn.length !== 11)}
        >
          {upsert.isPending ? (tr ? "Kaydediliyor…" : "Saving…") : tr ? "Kaydet" : "Save"}
        </button>
      </div>
      <p className="mono" style={{ color: "var(--mu)", fontSize: 10, marginTop: 12 }}>
        {tr
          ? "VKN/TCKN doğrulaması GIB algoritmasıyla yerel olarak yapılır — internet bağlantısı gerekmez."
          : "VKN/TCKN checksum is validated locally against the GIB algorithm — no external lookup."}
        {workspaceId ? "" : ""}
      </p>
    </div>
  );
}

function TaxIDStatusInline({ row, tr }: { row: TaxIDRow; tr: boolean }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 10,
        marginTop: 4,
        color: row.is_validated ? "var(--success)" : "var(--warning)",
      }}
    >
      {row.is_validated
        ? (tr ? "● kayıtlı ve doğrulandı" : "● on file, validated")
        : (tr ? "● kayıtlı (doğrulanamadı)" : "● on file (not validated)")}
    </div>
  );
}

// ── Account tab ────────────────────────────────────────────────────────────

function AccountTab({
  userEmail,
  hasPassword,
  tr,
}: {
  userEmail: string | undefined;
  hasPassword: boolean;
  tr: boolean;
}) {
  const nav = useNavigate();
  const signOut = useSignOut();
  const qc = useQueryClient();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const changePw = useMutation({
    mutationFn: () =>
      api<{ status: string }>("/auth/change-password", {
        method: "POST",
        body: hasPassword
          ? { current_password: current, new_password: next }
          : { new_password: next },
      }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setLocalErr(null);
      setDone(true);
      // has_password flips to true after a first-time set.
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const submit = () => {
    setDone(false);
    setLocalErr(null);
    if (next.length < 8) {
      setLocalErr(tr ? "Parola en az 8 karakter olmalı." : "Password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setLocalErr(tr ? "Parolalar eşleşmiyor." : "Passwords do not match.");
      return;
    }
    changePw.mutate();
  };

  const apiErr =
    changePw.error instanceof ApiError
      ? (changePw.error as ApiError).message
      : changePw.error
        ? String(changePw.error)
        : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          — {tr ? "Hesap" : "Account"}
        </div>
        <div style={{ fontSize: 14, color: "var(--tx)" }}>{userEmail ?? "—"}</div>
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {hasPassword ? (tr ? "Parola değiştir" : "Change password") : (tr ? "Parola belirle" : "Set password")}
        </div>

        {!hasPassword && (
          <div style={{ fontSize: 12, color: "var(--tx2)", marginBottom: 12, lineHeight: 1.5 }}>
            {tr
              ? "Google ile giriş yaptınız, bu yüzden mevcut parolanız yok. E-posta + parola ile de giriş yapabilmek için bir parola belirleyin."
              : "You signed in with Google, so you don't have a current password. Set one to also sign in with email + password."}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 360 }}>
          {hasPassword && (
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              placeholder={tr ? "Mevcut parola" : "Current password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder={tr ? "Yeni parola (en az 8 karakter)" : "New password (min 8 chars)"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder={tr ? "Yeni parola (tekrar)" : "Confirm new password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={changePw.isPending || next.length === 0}
            style={{ alignSelf: "flex-start" }}
          >
            {changePw.isPending
              ? (tr ? "Kaydediliyor…" : "Saving…")
              : hasPassword
                ? (tr ? "Parolayı güncelle" : "Update password")
                : (tr ? "Parolayı belirle" : "Set password")}
          </button>
        </div>

        {localErr || apiErr ? (
          <div style={{ color: "var(--error)", fontSize: 12, marginTop: 10 }} role="alert">
            {localErr ?? apiErr}
          </div>
        ) : null}
        {done ? (
          <div style={{ color: "var(--success)", fontSize: 12, marginTop: 10 }}>
            {tr ? "Parolanız güncellendi." : "Your password has been updated."}
          </div>
        ) : null}
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          — {tr ? "Oturum" : "Session"}
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={async () => {
            await signOut.mutateAsync();
            await nav({ to: "/sign-in" });
          }}
          disabled={signOut.isPending}
          style={{ color: "var(--error)" }}
        >
          {signOut.isPending ? (tr ? "Çıkılıyor…" : "Signing out…") : tr ? "Çıkış yap" : "Sign out"}
        </button>
      </div>
    </div>
  );
}

// ── Shared row + skeleton ──────────────────────────────────────────────────

function SettingRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card" style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        className="mono"
        style={{
          fontSize: 10,
          color: "var(--mu)",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      {children}
      {hint ? (
        <div style={{ fontSize: 11, color: "var(--tx2)", lineHeight: 1.5 }}>{hint}</div>
      ) : null}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="skel" style={{ height: 56, borderRadius: 12 }} />
      ))}
    </div>
  );
}
